import { groupFor, Property, PropertyGroup, sharedWith } from "."

export class InterfaceSelection {
  _selection: EntityPropertySelection

  constructor(selection: EntityPropertySelection) {
    this._selection = selection
  }

  inner(): Readonly<EntityPropertySelection> {
    return this._selection
  }

  static nothing() {
    return new InterfaceSelection({ sections: {}, regions: {}, everything: false })
  }

  static everything() {
    return new InterfaceSelection({ sections: {}, regions: {}, everything: true })
  }

  /**
   * Currently does not do merging of selections or other fancy stuff.
   */
  addPropertyId(propertyId: Property) {
    const groupId = groupFor(propertyId as Property)
    if (this.hasEntireGroup(groupId))
      return
    this._selection.sections[groupId] ??= {}
    for (const [start, end] of Object.entries(this._selection.sections[groupId]))
      if (start <= propertyId && end >= propertyId)
        return
    this._selection.sections[groupId][propertyId] = propertyId
  }

  hasPropertyId(propertyId: Property) {
    if (this._selection.everything)
      return true
    const groupId = groupFor(propertyId as Property)
    if (!isDefined(this._selection.sections[groupId]))
      return false
    if (!len(this._selection.sections[groupId]))
      return true
    for (const [start, end] of Object.entries(this._selection.sections[groupId]))
      if (start <= propertyId && end >= propertyId)
        return true
    return false
  }

  hasEntireGroup(groupId: PropertyGroup) {
    if (this._selection.everything)
      return true
    if (!isDefined(this._selection.sections[groupId]))
      return false
    if (!len(this._selection.sections[groupId]))
      return true
    return false
  }

  withSelfRole() {
    const { state: myself } = useSelf()
    if (myself.value.user)
      this.addPropertyId(sharedWith(myself.value.user.userId))
    return this
  }

  static selectedProperties = (
    properties: Array<Property>,
    groups: Array<PropertyGroup> = [],
  ) => {
    const selection = InterfaceSelection.nothing()
    for (const property of properties) {
      selection.addPropertyId(property)
    }
    for (const groupId of groups)
      selection.addEntireGroup(groupId)
    return selection
  }

  static selectedGroups = (groups: Array<PropertyGroup>) => {
    const selection = InterfaceSelection.nothing()
    for (const groupId of groups)
      selection.addEntireGroup(groupId)
    return selection
  }

  addEntireGroup(groupId: PropertyGroup) {
    if (this._selection.everything)
      return
    this._selection.sections[groupId] = {}
  }

  // NOTE: This does not work with overlapping selections yet. It will simply add
  // all ranges from both sides except when one of them defines that it selects
  // the entire range
  mergedWith(other: InterfaceSelection): InterfaceSelection {
    if (this._selection.everything || other._selection.everything)
      return InterfaceSelection.everything()
    for (const groupId of Object.keys(other._selection.sections)) {
      if (!len(other._selection.sections[groupId]!))
        // If the other selection has an empty group, we can just copy it over
        this._selection.sections[groupId] = {}
      else if (!isDefined(this._selection.sections[groupId]))
        this._selection.sections[groupId] = { ...other._selection.sections[groupId] }
      else if (len(this._selection.sections[groupId]!)) {
        // If both selections have the same group, we need to merge the ranges
        for (const [start, end] of Object.entries(other._selection.sections[groupId]!)) {
          if (!isDefined(this._selection.sections[groupId]![start]) || this._selection.sections[groupId]![start] < end) {
            this._selection.sections[groupId]![start] = end
          }
        }
      }
    }
    return this
  }

  static minimumPathSelection() {
    return InterfaceSelection.selectedProperties(
      [Property.FILE_MIME_TYPE, Property.FILE_NAME, Property.THUMBNAIL_SET, Property.FILE_NOTES],
      [PropertyGroup.FileSystemPathProperties],
    ).withSelfRole()
  }

  /**
   * @returns The minimum set of properties needed to show a file in a file item element,
   * such as in a history view, in the agent chat, etc.
   */
  static minimumFileItemSelection() {
    return InterfaceSelection.selectedProperties(
      [Property.FILE_MIME_TYPE, Property.FILE_NAME, Property.THUMBNAIL_SET],
      [PropertyGroup.FileSystemPathProperties],
    ).withSelfRole()
  }

  static minimumAutocompleteSelection() {
    return InterfaceSelection.selectedProperties(
      [Property.FILE_MIME_TYPE, Property.FILE_NAME, Property.THUMBNAIL_SET],
      [PropertyGroup.FileSystemPathProperties],
    ).withSelfRole()
  }

  /**
   * The minimum set of properties generally needed to show the file in the UI,
   * i.e. collection view.
   */
  static minimumInterfaceSelection() {
    return InterfaceSelection.selectedGroups([
      PropertyGroup.FolderDerivedSizingProperties,
      PropertyGroup.FileSystemPathProperties,
      PropertyGroup.FileCoreProperties,
      PropertyGroup.FileDerivedProperties,
      PropertyGroup.FileClientOnlyProperties,
    ]).withSelfRole()
  }

  /**
   * This seems intimidating but its actually a single db query since
   * these are all contiguous groups
   */
  static minimumPropertiesViewSelection() {
    return InterfaceSelection.selectedGroups([
      PropertyGroup.FolderDerivedSizingProperties,
      PropertyGroup.FileSystemPathProperties,
      PropertyGroup.FileCoreProperties,
      PropertyGroup.FileDerivedProperties,
      PropertyGroup.FileExtendedProperties,
      PropertyGroup.FileTagMetaProperty,
      PropertyGroup.FileFolderExtendedProperties,
      PropertyGroup.FileFolderExtendedDerivedProperties,
      PropertyGroup.FileImageExtendedProperties,
      PropertyGroup.FileImageExtendedDerivedProperties,
      PropertyGroup.FileAudioExtendedProperties,
      PropertyGroup.FileAudioExtendedDerivedProperties,
      PropertyGroup.FileVideoExtendedProperties,
      PropertyGroup.FileVideoExtendedDerivedProperties,
      PropertyGroup.FileThreeDimensionalExtendedProperties,
      PropertyGroup.FileThreeDimensionalExtendedDerivedProperties,
      PropertyGroup.FileLinkExtendedProperties,
      PropertyGroup.FileLinkExtendedDerivedProperties,
      PropertyGroup.FilePdfExtendedProperties,
      PropertyGroup.FilePdfExtendedDerivedProperties,
      PropertyGroup.FileTextExtendedProperties,
      PropertyGroup.FileTextExtendedDerivedProperties,
      PropertyGroup.FileSecondaryDerivedProperties,
    ]).withSelfRole()
  }

  /**
   * Minimum set of property groups needed to show a file in the transcript view in
   * the agent:
   *
   * mime type, file name, thumbnails.
   *
   * TODO: for shared folders this may include more things.
   */
  static minimumTranscriptSelection() {
    return InterfaceSelection.selectedProperties(
      [Property.FILE_NAME, Property.THUMBNAIL_SET],
      [PropertyGroup.FileSystemPathProperties],
    ).withSelfRole()
  }

  /**
   * The minimum set of properties that are required to get
   * information about a folder in order to do things like
   * navigation, etc.
   *
   * TODO: only retrieve notes if we have the notes view.
   * TODO: folder icons?
   */
  static minimumClientFolderProperties() {
    return InterfaceSelection.selectedProperties(
      [Property.FILE_NOTES],
      [PropertyGroup.FileFolderExtendedProperties],
    ).withSelfRole()
  }

  /**
   * The minimum selection for the user's SelfConversations
   * endpoint. Notably, we don't need the entire transcript.
   */
  static minimumSelfConversationSelection() {
    return InterfaceSelection.selectedGroups([
      PropertyGroup.ConversationCoreProperties,
      PropertyGroup.ConversationSharingUsersMetaProperty,
    ]).withSelfRole()
  }

  /**
   * The minimum set of properties when doing a conversation
   * properties request, e.g. when we load the conversation
   * on the sidebar.
   */
  static minimumConversationViewSelection() {
    return InterfaceSelection.selectedGroups([
      PropertyGroup.ConversationCoreProperties,
      PropertyGroup.ConversationTurnsMetaProperty,
      PropertyGroup.ConversationSharingUsersMetaProperty,
    ]).withSelfRole()
  }
}
